import {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { TLinkData } from '@utils/chatbot/types'
import useBodyScroll from '@hooks/useBodyScroll'
import useExecuteOnClientNavigation from '@hooks/useExecuteOnClientNavigation'
import useIsUserLoggedIn from '@hooks/useIsUserLoggedIn'
import usePageMetadata from '@hooks/usePageMetadata'
import useTracking from '@hooks/useTracking'
import useViewportType from '@hooks/useViewport/useViewportType'
import {
  ChatbotLastQuestion,
  ChatbotMessage,
  ChatbotQuestionData,
  ChatbotReloadQuestion,
  CustomEntrypoint,
} from '@components/Chatbot/types'
import { trackHandler } from '@components/Chatbot/utils'

import useChatbotLastQuestion from './useChatbotLastQuestion'
import { CHATBOT_RELOAD_QUESTION_KEY } from './useChatbotReloadQuestion'
import useChatbotVisible from './useChatbotVisible'

const SCROLL_CONTAINER_CLASS_NAME = 'chatbot-scroll-container'

const useChatbot = () => {
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()
  const unsubscribeFnRef = useRef<(() => void)[]>([])
  const isChatbotVisible = useChatbotVisible()
  const chatbotLastQuestion = useChatbotLastQuestion()
  const loggedIn = useIsUserLoggedIn()
  const pageMetadata = usePageMetadata()
  const viewportType = useViewportType()
  const isMobile = viewportType === 'mobile'
  const { lockBodyScroll, unlockBodyScroll } = useBodyScroll()
  const handleTracking = useTracking(trackHandler)

  const [chatbotInitialLoading, setChatbotInitialLoading] = useState(
    queryClient.getQueryData<boolean>(['chatbotInitialLoading']),
  )

  const [chatbotConversationId, setChatbotConversationId] = useState(
    queryClient.getQueryData<string | undefined>(['chatbotConversationId']),
  )

  const [chatbotInitialQuestions, setChatbotInitialQuestions] = useState(
    queryClient.getQueryData<ChatbotQuestionData[]>([
      'chatbotInitialQuestions',
    ]) || [],
  )

  const [chatbotMessages, setChatbotMessages] = useState<ChatbotMessage[]>(
    queryClient.getQueryData<ChatbotMessage[]>(['chatbotMessages']) || [],
  )

  const [chatbotAnswerLoading, setChatbotAnswerLoading] = useState(
    queryClient.getQueryData<boolean>(['chatbotAnswerLoading']),
  )

  const [chatbotInitialLinks, setChatbotInitialLinks] =
    useState<TLinkData | null>(
      queryClient.getQueryData<TLinkData>(['chatbotInitialLinks']) || null,
    )
  const showLoginBox = useMemo(
    () => !loggedIn && !chatbotAnswerLoading && chatbotMessages.length > 1,
    [loggedIn, chatbotAnswerLoading, chatbotMessages],
  )

  const openChatbot = useCallback(
    (entryPoint?: CustomEntrypoint) => {
      queryClient.setQueryData(['chatbotVisible'], true)
      queryClient.invalidateQueries({
        queryKey: ['chatbotVisible'],
      })

      handleTracking({ event: 'chatbot_open', customEntrypoint: entryPoint })

      if (isMobile) {
        lockBodyScroll()
      }

      if (chatbotMessages.length) {
        const question = JSON.parse(
          sessionStorage.getItem('question-old') || 'null',
        )
        if (question) {
          sessionStorage.setItem('question', JSON.stringify(question))
          sessionStorage.removeItem('question-old')
        }
      }
    },
    [queryClient, chatbotMessages, isMobile, lockBodyScroll, handleTracking],
  )

  const chatbotCloseCleanup = () => {
    if (isMobile) {
      unlockBodyScroll()
    }

    const question = JSON.parse(sessionStorage.getItem('question') || 'null')
    if (question) {
      sessionStorage.setItem('question-old', JSON.stringify(question))
      sessionStorage.removeItem('question')
    }
  }

  const closeChatbot = () => {
    queryClient.setQueryData(['chatbotVisible'], false)
    queryClient.invalidateQueries({
      queryKey: ['chatbotVisible'],
    })

    chatbotCloseCleanup()
  }

  useExecuteOnClientNavigation(chatbotCloseCleanup)

  const onQuestionClick = (
    selectedQuestion: ChatbotQuestionData,
    options?: { conversationId?: string; entryPoint?: CustomEntrypoint },
  ) => {
    queryClient.setQueryData<ChatbotLastQuestion>(['chatbotLastQuestion'], {
      data: selectedQuestion,
      chatbotConversationId: options?.conversationId,
      entryPoint: options?.entryPoint,
    })
    queryClient.invalidateQueries({
      queryKey: ['chatbotLastQuestion'],
    })

    if (!isChatbotVisible) {
      openChatbot(options?.entryPoint)
    }

    if (
      sessionStorage &&
      !loggedIn &&
      selectedQuestion?.isSuggestedQuestion &&
      selectedQuestion?.isCached
    ) {
      sessionStorage.setItem('question', JSON.stringify(selectedQuestion))
    }
  }

  const onQuestionSubmit = (
    question: string,
    options?: { conversationId?: string; entryPoint?: CustomEntrypoint },
  ) => {
    queryClient.setQueryData<ChatbotLastQuestion>(['chatbotLastQuestion'], {
      data: {
        id: `user-question-${new Date().getTime()}`,
        text: question,
        isCached: false,
        isSuggestedQuestion: false,
        context: pageMetadata?.id || 'home',
      },
      chatbotConversationId: options?.conversationId,
    })
    queryClient.invalidateQueries({
      queryKey: ['chatbotLastQuestion'],
    })

    if (!isChatbotVisible) {
      openChatbot(options?.entryPoint)
    }
  }

  const onQuestionReload = (
    question: { text: string; id: string },
    answerId: string,
    conversationId?: string,
  ) => {
    queryClient.setQueryData<ChatbotReloadQuestion>(
      [CHATBOT_RELOAD_QUESTION_KEY],
      {
        data: {
          id: question.id,
          text: question.text,
          isCached: false,
          isSuggestedQuestion: false,
          context: pageMetadata?.id || 'home',
        },
        chatbotConversationId: conversationId,
        chatbotAnswerId: answerId,
      },
    )
    queryClient.invalidateQueries({
      queryKey: [CHATBOT_RELOAD_QUESTION_KEY],
    })
  }

  const resetMessages = () => {
    queryClient.setQueryData(['chatbotMessages'], [])
    queryClient.invalidateQueries({
      queryKey: ['chatbotMessages'],
    })
  }

  const isLastQuestion = (id: string) => {
    return chatbotLastQuestion?.data.context === id
  }

  const updateChabotCommon = useCallback(
    (
      args: any,
      key: string,
      setState: (value: SetStateAction<any | undefined>) => void,
    ) => {
      if (
        args?.action?.type === 'invalidate' &&
        args.query.queryKey[0] === key
      ) {
        setState(queryClient.getQueryData([key]))
      }
    },
    [queryClient],
  )

  useEffect(() => {
    const unsubscribeFnRefCurrent = unsubscribeFnRef.current

    unsubscribeFnRefCurrent?.push(
      queryCache.subscribe((args: any) =>
        updateChabotCommon(
          args,
          'chatbotInitialLoading',
          setChatbotInitialLoading,
        ),
      ),
    )

    unsubscribeFnRefCurrent?.push(
      queryCache.subscribe((args: any) =>
        updateChabotCommon(
          args,
          'chatbotConversationId',
          setChatbotConversationId,
        ),
      ),
    )

    unsubscribeFnRefCurrent?.push(
      queryCache.subscribe((args: any) =>
        updateChabotCommon(
          args,
          'chatbotInitialQuestions',
          setChatbotInitialQuestions,
        ),
      ),
    )

    unsubscribeFnRefCurrent?.push(
      queryCache.subscribe((args: any) =>
        updateChabotCommon(args, 'chatbotInitialLinks', setChatbotInitialLinks),
      ),
    )

    unsubscribeFnRefCurrent?.push(
      queryCache.subscribe((args: any) =>
        updateChabotCommon(args, 'chatbotMessages', setChatbotMessages),
      ),
    )

    unsubscribeFnRefCurrent?.push(
      queryCache.subscribe((args: any) =>
        updateChabotCommon(
          args,
          'chatbotAnswerLoading',
          setChatbotAnswerLoading,
        ),
      ),
    )

    return () => {
      if (unsubscribeFnRefCurrent) {
        unsubscribeFnRefCurrent.forEach((unsubscribe) => unsubscribe())
      }
    }
  }, [queryCache, updateChabotCommon])

  useEffect(() => {
    if (!chatbotLastQuestion && loggedIn) {
      const question = JSON.parse(sessionStorage.getItem('question') || 'null')
      if (question) {
        openChatbot('open bliki')
        queryClient.setQueryData<ChatbotLastQuestion>(['chatbotLastQuestion'], {
          data: question,
          preventTyping: true,
        })
        queryClient.invalidateQueries({
          queryKey: ['chatbotLastQuestion'],
        })
        sessionStorage.removeItem('question')
      }
    }
  }, [loggedIn, queryClient, chatbotLastQuestion, openChatbot])

  return {
    openChatbot,
    closeChatbot,
    chatbotInitialLoading,
    chatbotConversationId,
    chatbotInitialQuestions,
    chatbotInitialLinks,
    onQuestionClick,
    onQuestionSubmit,
    onQuestionReload,
    chatbotMessages,
    chatbotAnswerLoading,
    showLoginBox,
    SCROLL_CONTAINER_CLASS_NAME,
    resetMessages,
    isLastQuestion,
  }
}

export default useChatbot
